/**
 * Task #2174 — Zod schemas per i nuovi boundary HTTP del Security Center
 * Fail2Ban (whitelist con TTL, audit export, diagnostica dry-run).
 *
 * Source of truth dei contratti consumati da `validate(...)` lato BE e da
 * `z.infer<>` lato FE. Niente duplicazione manuale dei tipi.
 */
import { z } from 'zod';

// IPv4 / IPv6 minimale (coerente con fail2ban.routes.ts validIp()).
const IPV4_RE = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
export const f2bIpSchema = z
  .string()
  .trim()
  .refine(
    (v) => IPV4_RE.test(v) || (/^[0-9a-fA-F:]+$/.test(v) && v.includes(':')),
    { message: 'IP non valido' },
  );

export const f2bNodeIdSchema = z.enum([
  'fe1', 'be1', 'be2', 'db1', 'db2', 'redis1', 'be3', 'be4', 'kam1', 'obs1', 'lb1',
]);
export type Fail2BanNodeIdDto = z.infer<typeof f2bNodeIdSchema>;

// ─── Whitelist entries (SoT public.fail2ban_whitelist) ────────────────────
export const f2bWhitelistAddSchema = z.object({
  ip: f2bIpSchema,
  reason: z.string().trim().max(500).optional().default(''),
  nodes: z.array(f2bNodeIdSchema).min(1).optional(),
  /** Scadenza ISO8601. NULL/assente = permanente. */
  expires_at: z.string().datetime({ offset: true }).optional().nullable(),
});
export type Fail2BanWhitelistAddDto = z.infer<typeof f2bWhitelistAddSchema>;

export const f2bWhitelistDeleteParamsSchema = z.object({
  ip: f2bIpSchema,
});

export interface Fail2BanWhitelistEntryDto {
  ip: string;
  reason: string;
  nodes: string[];
  expires_at: string | null;
  created_by: string | null;
  created_by_email: string | null;
  created_at: string;
}

// ─── Audit export CSV ─────────────────────────────────────────────────────
export const f2bAuditExportQuerySchema = z.object({
  action: z.string().trim().min(1).max(64).optional(),
  actor_email: z.string().trim().max(255).optional(),
  target_ip: f2bIpSchema.optional(),
  since: z.string().datetime({ offset: true }).optional(),
  limit: z.coerce.number().int().positive().max(50000).optional().default(5000),
});
export type Fail2BanAuditExportQueryDto = z.infer<typeof f2bAuditExportQuerySchema>;

// ─── Diagnostica: dry-run unban (2FA) ─────────────────────────────────────
export const f2bDryRunUnbanSchema = z.object({
  ip: f2bIpSchema,
  nodes: z.array(f2bNodeIdSchema).min(1).optional(),
  totpCode: z.string().min(6).max(8).optional(),
  backupCode: z.string().min(8).max(64).optional(),
}).refine((d) => Boolean(d.totpCode || d.backupCode), {
  message: 'totpCode o backupCode richiesto',
});
export type Fail2BanDryRunUnbanDto = z.infer<typeof f2bDryRunUnbanSchema>;

export interface Fail2BanDryRunUnbanResultDto {
  ip: string;
  banned: boolean;
  nodes: Array<{
    node_id: string;
    would_unban: boolean;
    jails: string[];
    reachable: boolean;
    latency_ms: number;
  }>;
}

// ─── Services health (read-only) ──────────────────────────────────────────
export interface Fail2BanServicesHealthDto {
  aggregator: {
    nodes_total: number;
    nodes_online: number;
    nodes_stale: number;
    last_heartbeat_at: string | null;
    stale_nodes: string[];
  };
  broadcast: {
    pending_commands: number;
    last_action_at: string | null;
    last_action: string | null;
    last_failure_at: string | null;
  };
  degraded: boolean;
}

// ─── Jail Manager (Task #2215) ────────────────────────────────────────────
/** Vista per-nodo di una jail (GET /jails?server=<node>). */
export interface Fail2BanJailDto {
  name: string;
  enabled: boolean;
  maxretry: number;
  findtime: number;
  bantime: number;
  currentlyBanned: number;
  totalBanned: number;
  filter: string;
  logpath: string;
  port: string;
  bannedIPs: string[];
}

/** Presenza/config di una jail su un singolo nodo (vista cluster). */
export interface Fail2BanClusterJailNodeDto {
  node_id: Fail2BanNodeIdDto;
  enabled: boolean;
  currentlyBanned: number;
  totalBanned: number;
  maxretry: number | null;
  findtime: number | null;
  bantime: number | null;
  logpath: string | null;
  is_stale: boolean;
}
/** Vista cluster aggregata di una jail (GET /jails-cluster). */
export interface Fail2BanClusterJailDto {
  name: string;
  present_on: Fail2BanNodeIdDto[];
  total_currently_banned: number;
  total_banned: number;
  enabled_anywhere: boolean;
  nodes: Fail2BanClusterJailNodeDto[];
}

/** Edit config jail (PUT /jails/:name/config). Almeno un parametro richiesto. */
export const f2bJailConfigSchema = z.object({
  maxRetry: z.coerce.number().int().positive().optional(),
  findTime: z.coerce.number().int().positive().optional(),
  banTime: z.coerce.number().int().optional(), // -1 = permanente
  nodes: z.array(f2bNodeIdSchema).min(1).optional(),
}).refine(
  (d) => d.maxRetry !== undefined || d.findTime !== undefined || d.banTime !== undefined,
  { message: 'Almeno un parametro (maxRetry/findTime/banTime) richiesto' },
);
export type Fail2BanJailConfigDto = z.infer<typeof f2bJailConfigSchema>;

/** Toggle enable/disable jail (POST /jails/:name/enable|disable). */
export const f2bJailToggleSchema = z.object({
  nodes: z.array(f2bNodeIdSchema).min(1).optional(),
});
export type Fail2BanJailToggleDto = z.infer<typeof f2bJailToggleSchema>;

// Create/delete jail: solo app node (fe1/be1/be2 hanno backend Node).
const f2bAppNodeIdSchema = z.enum(['fe1', 'be1', 'be2']);
export const f2bJailCreateSchema = z.object({
  name: z.string().trim().regex(/^[a-zA-Z0-9_-]+$/, 'Solo lettere/numeri/underscore/trattino').max(64),
  port: z.string().trim().min(1).max(128),
  filter: z.string().trim().max(128).optional(),
  logPath: z.string().trim().max(1024).optional(),
  maxRetry: z.coerce.number().int().positive().default(5),
  findTime: z.coerce.number().int().positive().default(600),
  banTime: z.coerce.number().int().default(3600),
  nodes: z.array(f2bAppNodeIdSchema).min(1),
});
export type Fail2BanJailCreateDto = z.infer<typeof f2bJailCreateSchema>;

export const f2bJailDeleteSchema = z.object({
  jail: z.string().trim().min(1).max(64),
  nodes: z.array(f2bAppNodeIdSchema).min(1),
});
export type Fail2BanJailDeleteDto = z.infer<typeof f2bJailDeleteSchema>;

/** Esito broadcast per-nodo (riusato da ban/unban/jail ops). */
export interface Fail2BanNodeOutcomeDto {
  status: 'ok' | 'queued' | 'failed';
  error?: string | null;
  duration_ms?: number;
}
export interface Fail2BanBroadcastResultDto {
  action: string;
  audit_id: string;
  target_ip?: string | null;
  target_jail?: string | null;
  nodes_targeted: Fail2BanNodeIdDto[];
  outcome_per_node: Record<string, Fail2BanNodeOutcomeDto>;
  success_count: number;
  failure_count: number;
  queued_count: number;
  duration_ms: number;
}

// ─── Promote recidivo → ban fail2ban permanente (2FA) ──────────────────────
// Task #2206 — ex secfilter blacklist: ora promozione a ban fail2ban permanente.
export const fail2banPromoteRecidivoSchema = z.object({
  ips: z.array(f2bIpSchema).min(1).max(500),
  reason: z.string().trim().min(3).max(500),
  totpCode: z.string().min(6).max(8).optional(),
  backupCode: z.string().min(8).max(64).optional(),
}).refine((d) => Boolean(d.totpCode || d.backupCode), {
  message: 'totpCode o backupCode richiesto',
});
export type Fail2banPromoteRecidivoDto = z.infer<typeof fail2banPromoteRecidivoSchema>;
